import { useContext, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import MerchantContext from "../../contexts/MerchantContext";
import MainSectionSection from "../../components/MainSectionSection";
import ProjectFormDialog from "../../components/settings/ProjectFormDialog";
import { trpc, RouterOutputs } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useIsMobile } from "@/hooks/useMediaQuery";

type Project = RouterOutputs["projects"]["list"][number];

const ProjectsConfigurationContainer = () => {
  const { merchantId, merchantType, isLoading: isLoadingContext } = useContext(MerchantContext);
  const isMobile = useIsMobile();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const isDevelopment = merchantType === "development";

  const projects = trpc.projects.list.useQuery(
    { merchantId },
    { enabled: !!merchantId && isDevelopment }
  );

  if (isLoadingContext) {
    return (
      <MainSectionSection>
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" />
        </div>
      </MainSectionSection>
    );
  }

  if (!isDevelopment) {
    return (
      <MainSectionSection>
        <div className="flex min-h-[200px] items-center justify-center p-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg text-gray-500">Esta sección solo aplica a clientes de desarrollos</p>
            <p className="text-sm text-gray-400">
              La gestión de proyectos está disponible para merchants de tipo desarrollo.
            </p>
          </div>
        </div>
      </MainSectionSection>
    );
  }

  const handleOpenCreate = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (project: Project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const projectsData = projects.data ?? [];

  return (
    <MainSectionSection>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800 md:text-2xl">Proyectos</h1>
        <Button onClick={handleOpenCreate} size="sm">
          <Plus className="mr-1 size-4" />
          Crear proyecto
        </Button>
      </div>

      {projects.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" />
        </div>
      ) : projectsData.length === 0 ? (
        <div className="flex min-h-[200px] items-center justify-center p-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-lg text-gray-500">No hay proyectos creados</p>
            <p className="text-sm text-gray-400">
              Crea un proyecto para asociarlo a un agente.
            </p>
          </div>
        </div>
      ) : isMobile ? (
        <div className="flex flex-col gap-3">
          {projectsData.map((project) => (
            <div
              key={project.id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <p className="truncate font-semibold text-gray-800">{project.name}</p>
                  <p className="truncate font-mono text-xs text-gray-500">{project.code}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEdit(project)}
                  className="size-8 shrink-0"
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="flex border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-600">
            <div style={{ flex: "0 0 55%" }}>Nombre</div>
            <div style={{ flex: "0 0 35%" }}>Código</div>
            <div style={{ flex: "0 0 10%" }}>Acciones</div>
          </div>
          {projectsData.map((project) => (
            <div
              key={project.id}
              className="flex items-center border-b border-gray-100 px-4 py-3 last:border-b-0"
            >
              <div style={{ flex: "0 0 55%" }} className="truncate font-medium text-gray-800">
                {project.name}
              </div>
              <div style={{ flex: "0 0 35%" }} className="truncate font-mono text-sm text-gray-600">
                {project.code}
              </div>
              <div style={{ flex: "0 0 10%" }} className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenEdit(project)}
                  className="size-8"
                >
                  <Pencil className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ProjectFormDialog
        isOpen={isModalOpen}
        onClose={handleClose}
        merchantId={merchantId}
        editingProject={editingProject}
      />
    </MainSectionSection>
  );
};

export default ProjectsConfigurationContainer;
